// Lightweight Web-Audio sound utilities — no external assets. Synthesizes
// short tones via OscillatorNode so the bundle stays tiny and there's zero
// network latency.

let _ctx = null;

function getCtx() {
  if (typeof window === "undefined") return null;
  try {
    if (!_ctx) {
      const AudioCtor = window.AudioContext || window.webkitAudioContext;
      if (!AudioCtor) return null;
      _ctx = new AudioCtor();
    }
    if (_ctx.state === "suspended") {
      // Browser autoplay policy needs a user gesture before resume succeeds.
      // We call .resume() best-effort; if blocked, the next call after a real
      // user click will succeed.
      _ctx.resume().catch(() => {});
    }
    return _ctx;
  } catch (_) {
    return null;
  }
}

/**
 * playTone — single short note.
 * @param {number} freq      Hz (e.g. 880 = A5)
 * @param {number} durMs     Length in milliseconds
 * @param {number} volume    0..1
 * @param {string} type      "sine" | "triangle" | "square" | "sawtooth"
 * @param {number} startAt   delay in seconds before playback
 */
function playTone({ freq, durMs = 140, volume = 0.18, type = "sine", startAt = 0 }) {
  const ctx = getCtx();
  if (!ctx) return;
  const now = ctx.currentTime + startAt;
  const osc = ctx.createOscillator();
  const gain = ctx.createGain();
  osc.type = type;
  osc.frequency.setValueAtTime(freq, now);
  // Soft envelope so it doesn't click
  gain.gain.setValueAtTime(0.0001, now);
  gain.gain.exponentialRampToValueAtTime(volume, now + 0.012);
  gain.gain.exponentialRampToValueAtTime(0.0001, now + durMs / 1000);
  osc.connect(gain).connect(ctx.destination);
  osc.start(now);
  osc.stop(now + durMs / 1000 + 0.02);
}

/**
 * Pleasant rising two-tone chime to mark the start of a chat session.
 * Plays a perfect-fifth-up (C5 → G5) with a soft sine envelope.
 */
export function playStartChime() {
  // Two stacked sine tones for a warmer "bell" texture
  playTone({ freq: 523.25, durMs: 260, volume: 0.16, type: "sine" });
  playTone({ freq: 783.99, durMs: 260, volume: 0.13, type: "sine", startAt: 0.05 });
  playTone({ freq: 1046.5, durMs: 380, volume: 0.10, type: "sine", startAt: 0.15 });
}

/**
 * Short "tick" — used in the final 5-second countdown.
 * Higher pitch on the final tick (1s) so the user clearly perceives "0 imminent".
 */
export function playTick(isFinal = false) {
  playTone({
    freq: isFinal ? 1200 : 900,
    durMs: isFinal ? 220 : 100,
    volume: isFinal ? 0.25 : 0.18,
    type: "triangle",
  });
}

/** Resumes the AudioContext (call after a user gesture if a chime didn't fire). */
export function unlockAudio() {
  getCtx();
}
